import classNames from "classnames";
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  type MouseEvent,
  type ReactNode
} from "react";
import { ModalAspectRatio } from "./modalAspectRatio";

interface ModalProps {
  children?: ReactNode;
  additionalClass?: string;
  hasFooter?: boolean;
  active?: boolean;
  allowOverflow?: boolean;
  aspectRatio?: ModalAspectRatio;
  onClose?: () => void;
  titleId?: string;
  modalName?: string;
}

interface ModalHandle {
  focus: () => void;
  blur: () => void;
}

const FOCUSABLE_SELECTORS = classNames(
  "a[href]",
  "area[href]",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "button:not([disabled])",
  "iframe",
  "object",
  "embed",
  '[tabindex]:not([tabindex="-1"])',
  "[contenteditable]"
);

export const Modal = forwardRef<ModalHandle, ModalProps>(
  (
    {
      children,
      additionalClass,
      hasFooter = false,
      active = false,
      allowOverflow = false,
      aspectRatio = ModalAspectRatio.None,
      onClose,
      titleId,
      modalName
    },
    ref
  ) => {
    const modalRef = useRef<HTMLDivElement>(null);
    const lastActiveElementRef = useRef<Element | null>(null);

    // Prevent body scroll when modal is active
    useEffect(() => {
      if (active) {
        document.documentElement.style.overflowY = "hidden";
        return () => {
          document.documentElement.style.overflowY = "";
        };
      }
    }, [active]);

    // Global focus trap
    useEffect(() => {
      if (!active || !modalRef.current) {
        return;
      }

      let isHandlingFocus = false;

      const handleDocumentFocus = (e: FocusEvent) => {
        if (isHandlingFocus) {
          return;
        }
        isHandlingFocus = true;
        try {
          const focusedElement = e.target as HTMLElement | null;
          if (
            focusedElement &&
            modalRef.current &&
            !modalRef.current.contains(focusedElement)
          ) {
            const focusableEls =
              modalRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTORS);
            const focusable = Array.from(focusableEls).filter(
              el => (el as HTMLElement).offsetParent !== null
            );
            if (focusable.length > 0) {
              (focusable[0] as HTMLElement).focus();
            } else {
              modalRef.current.focus();
            }
          }
        } finally {
          isHandlingFocus = false;
        }
      };

      document.addEventListener("focus", handleDocumentFocus, true);
      return () => {
        document.removeEventListener("focus", handleDocumentFocus, true);
      };
    }, [active]);

    // Handle escape key to close modal
    useEffect(() => {
      if (!active) {
        return;
      }

      const handleEscape = (event: KeyboardEvent) => {
        if (event.key === "Escape" && onClose) {
          onClose();
        }
      };

      document.addEventListener("keydown", handleEscape);
      return () => document.removeEventListener("keydown", handleEscape);
    }, [active, onClose]);

    // Save last focused element and focus modal on open
    useEffect(() => {
      if (!active || !modalRef.current) {
        return;
      }

      lastActiveElementRef.current = document.activeElement;
      modalRef.current.focus();
    }, [active]);

    // Keyboard tab trap inside modal
    useEffect(() => {
      if (!active || !modalRef.current) {
        return;
      }
      const node = modalRef.current;

      const handleTab = (e: KeyboardEvent) => {
        if (e.key !== "Tab") {
          return;
        }

        const focusableEls = node.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTORS);
        const focusable = Array.from(focusableEls).filter(
          el => (el as HTMLElement).offsetParent !== null
        );

        if (focusable.length === 0) {
          return;
        }

        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          (first as HTMLElement).focus();
        } else if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          (last as HTMLElement).focus();
        }
      };

      node.addEventListener("keydown", handleTab);
      return () => {
        node.removeEventListener("keydown", handleTab);
      };
    }, [active]);

    // Restore focus to trigger element on close
    useEffect(() => {
      if (!active && lastActiveElementRef.current instanceof HTMLElement) {
        lastActiveElementRef.current.focus();
      }
    }, [active]);

    const backdropClasses = useMemo(
      () =>
        classNames(
          "fixed top-0 left-0 flex justify-center items-center",
          "w-full h-full p-xl z-modal",
          "bg-black/15",
          "transition-long-out cursor-pointer",
          "opacity-0 pointer-events-none",
          {
            "!opacity-100 !pointer-events-auto !transition-long-in": active,
            "overflow-y-auto": allowOverflow,
            "aspect-video": aspectRatio === ModalAspectRatio.Video
          },
          additionalClass
        ),
      [active, allowOverflow, additionalClass, aspectRatio]
    );

    const modalContentClasses = useMemo(
      () =>
        classNames(
          "relative -top-xl max-w-full max-h-[80vh]",
          "p-lg sm:p-xl lg:p-3xl overflow-y-auto cursor-default",
          "bg-white rounded-tl-br-lg shadow-xs",
          "sm:p-[2.5rem]",
          "lg:max-w-[1280px] xl:max-w-[1440px]",
          {
            "!overflow-y-visible":
              aspectRatio === ModalAspectRatio.Video || allowOverflow,
            "pb-0 sm:pb-0 lg:pb-0": hasFooter
          }
        ),
      [aspectRatio, allowOverflow, hasFooter]
    );

    const aspectWrapperClasses = useMemo(
      () =>
        classNames(
          "flex justify-center items-center max-h-[80vh]",
          // Height calculation based on width minus padding (simulating SCSS calc)
          "h-[calc((100vw-4rem)*0.5625)]", // Using 4rem (xl spacing) for padding
          "aspect-video",
          "lg:h-[calc((1280px-4rem)*0.5625)]",
          "xl:h-[calc((1440px-4rem)*0.5625)]"
        ),
      []
    );

    const handleBackdropClick = useCallback(
      (event: MouseEvent<HTMLDivElement>) => {
        if (onClose && event.target === event.currentTarget) {
          onClose();
        }
      },
      [onClose]
    );

    // Handle modal content click (stop propagation for non-video modals)
    const handleContentClick = useCallback(
      (event: MouseEvent<HTMLDivElement>) => {
        if (aspectRatio !== ModalAspectRatio.Video) {
          event.stopPropagation();
        }
      },
      [aspectRatio]
    );

    // Imperative handle methods
    const focus = useCallback(() => {
      modalRef.current?.focus();
    }, []);

    const blur = useCallback(() => {
      modalRef.current?.blur();
    }, []);

    useImperativeHandle(
      ref,
      () => ({
        focus,
        blur
      }),
      [focus, blur]
    );

    if (!active) {
      return null;
    }

    return (
      <div className={backdropClasses} onClick={handleBackdropClick}>
        <div
          className={modalContentClasses}
          ref={modalRef}
          onClick={handleContentClick}
          tabIndex={-1}
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          data-modal-scrollable-content
          data-online-booking-modal-active={active ? "true" : "false"}
          {...(modalName && { "data-online-booking-modal": modalName })}
        >
          {aspectRatio === ModalAspectRatio.Video ? (
            <div className={aspectWrapperClasses}>{children}</div>
          ) : (
            children
          )}
        </div>
      </div>
    );
  }
);

Modal.displayName = "Modal";

export default Modal;
export type { ModalHandle };
